import { useState, useEffect, forwardRef } from 'react'
import { 
    Table, 
    TableContainer, 
    TableCell,
    TableHead, 
    TableBody,
    TableRow 
} from "@mui/material"
import { growthRate, formatCurrency, isNegative } from '../../helpers/process_morningstar'

const periods = [
    "5D",
    "1M",
    "6M",
    "YTD",
    "2Y CAGR",
    "5Y CAGR",
    "10Y CAGR",
    "",
    "Change"
]

function CAGR(currentPrice, startPrice, years) {
  return ((currentPrice/startPrice)**(1/years) - 1) * 100
}

const ReturnsTable = forwardRef(({twoYearData, data}, ref) => {
    const [twoYearPrice, setTwoYearPrice] = useState([])
    const [fiveYearPrice, setFiveYearPrice] = useState([])
    const [returnData, setReturnData] = useState([])

    useEffect(() => {
      const fetchStockData = async () => {
        const twoYear = await twoYearData
        const fiveYear = await data
        setTwoYearPrice(twoYear)
        setFiveYearPrice(fiveYear)
      }

      fetchStockData()
    }, [twoYearData, data])

    useEffect(() => {
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;
      const currentPrice = fiveYearPrice[fiveYearPrice.length - 1]
      const currentPriceIncl = twoYearPrice[twoYearPrice.length - 1]

        const fiveDays = twoYearPrice.slice(-5);
        const oneMonth = fiveYearPrice.slice(-2);
        const sixMonth = fiveYearPrice.slice(-7);
        const thisYear = twoYearPrice.filter((p) =>
                new Date(p.date).getFullYear() === new Date().getFullYear()
              );
        const lastPrevYear = twoYearPrice
            .filter((p) => new Date(p.date).getFullYear() === previousYear)
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        const ytd = lastPrevYear
          ? [lastPrevYear, ...thisYear]
          : thisYear;
        const twoYear = twoYearPrice;
        const fiveYear = fiveYearPrice.slice(-61)
        const tenYear = fiveYearPrice.slice(-121)
        const fiftyTwoWeek = twoYearPrice.slice(-261)
        const inclArray = []
        const exclArray = []


        const fiftyTwoPriceHigh = Math.max(...fiftyTwoWeek.map(obj => obj.ExcludingDividends))
        const fiftyTwoPriceLow = Math.min(...fiftyTwoWeek.map(obj => obj.ExcludingDividends))

        if (fiveDays.length > 1) {
          const fiveDayGrowthIncl = (fiveDays[fiveDays.length - 1]['ReinvestedDividends'] / fiveDays[0]['ReinvestedDividends'] - 1) * 100
          const fiveDayGrowthExcl = (fiveDays[fiveDays.length - 1]['ExcludingDividends'] / fiveDays[0]['ExcludingDividends'] - 1) * 100
          inclArray.push(fiveDayGrowthIncl.toFixed(1) + "%")
          exclArray.push(fiveDayGrowthExcl.toFixed(1) + "%")

          const oneMonthGrowthIncl = (oneMonth[oneMonth.length - 1]['ReinvestedDividends'] / oneMonth[0]['ReinvestedDividends'] - 1) * 100
          const oneMonthGrowthExcl = (oneMonth[oneMonth.length - 1]['ExcludingDividends'] / oneMonth[0]['ExcludingDividends'] - 1) * 100
          inclArray.push(oneMonthGrowthIncl.toFixed(1) + "%")
          exclArray.push(oneMonthGrowthExcl.toFixed(1) + "%")

          const sixMonthGrowthIncl = (sixMonth[sixMonth.length - 1]['ReinvestedDividends'] / sixMonth[0]['ReinvestedDividends'] - 1) * 100
          const sixMonthGrowthExcl = (sixMonth[sixMonth.length - 1]['ExcludingDividends'] / sixMonth[0]['ExcludingDividends'] - 1) * 100

          inclArray.push(sixMonthGrowthIncl.toFixed(1) + "%")
          exclArray.push(sixMonthGrowthExcl.toFixed(1) + "%")

          const ytdGrowthIncl = (ytd[ytd.length - 1]['ReinvestedDividends'] / ytd[0]['ReinvestedDividends'] - 1) * 100
          const ytdGrowthExcl = (ytd[ytd.length - 1]['ExcludingDividends'] / ytd[0]['ExcludingDividends'] - 1) * 100
          inclArray.push(ytdGrowthIncl.toFixed(1) + "%")
          exclArray.push(ytdGrowthExcl.toFixed(1) + "%")

          const twoYearGrowthIncl = CAGR(twoYear[twoYear.length - 1]['ReinvestedDividends'], twoYear[0]['ReinvestedDividends'], 2)
          const twoYearGrowthExcl = CAGR(twoYear[twoYear.length - 1]['ExcludingDividends'], twoYear[0]['ExcludingDividends'], 2)
          inclArray.push(twoYearGrowthIncl.toFixed(1) + "%")
          exclArray.push(twoYearGrowthExcl.toFixed(1) + "%")

          if (fiveYearPrice.length < 61) {
            inclArray.push('N/A')
            exclArray.push('N/A')
          } else {
          const fiveYearGrowthIncl = CAGR(fiveYear[fiveYear.length - 1]['ReinvestedDividends'], fiveYear[0]['ReinvestedDividends'], 5)
          const fiveYearGrowthExcl = CAGR(fiveYear[fiveYear.length - 1]['ExcludingDividends'], fiveYear[0]['ExcludingDividends'], 5)
          inclArray.push(fiveYearGrowthIncl.toFixed(1) + "%")
          exclArray.push(fiveYearGrowthExcl.toFixed(1) + "%")
          }

          if (fiveYearPrice.length < 121) {
            inclArray.push('N/A')
            exclArray.push('N/A')
          } else {
            const tenYearGrowthIncl = CAGR(tenYear[tenYear.length - 1]['ReinvestedDividends'], tenYear[0]['ReinvestedDividends'], 10)
            const tenYearGrowthExcl = CAGR(tenYear[tenYear.length - 1]['ExcludingDividends'], tenYear[0]['ExcludingDividends'], 10)
            inclArray.push(tenYearGrowthIncl.toFixed(1) + "%")
            exclArray.push(tenYearGrowthExcl.toFixed(1) + "%")
          }
        
          inclArray.push("52 Wk Low")
          exclArray.push("52 Wk High")

          const fiftyTwoHigh = (currentPrice.ExcludingDividends / fiftyTwoPriceHigh - 1) * 100
          const fiftyTwoLow = (currentPrice.ExcludingDividends / fiftyTwoPriceLow - 1) * 100
          inclArray.push(fiftyTwoLow.toFixed(1) + "%")
          exclArray.push(fiftyTwoHigh.toFixed(1) + "%")

          setReturnData({
            exclDiv: exclArray, 
            inclDiv: inclArray
          })
      }
    }, [twoYearPrice, fiveYearPrice])

    return (
        <TableContainer ref={ref} >
            <Table size="small">
                <TableHead>
                    {[{ label : "period", value : "index" }].map((period, id) => (
                      <TableRow>
                        <TableCell 
                          padding="none"
                          sx={{ 
                            borderBottom: "1px solid #ffffffff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            fontSize: {
                              xs: "7px",
                              sm: "9px",
                              md: "10px",
                            },
                            maxWidth: "15px",
                            padding: 0
                          }}
                          key={id}
                        >
                          Returns
                        </TableCell>
                        {periods.map((p) => (
                          <TableCell 
                            padding="none"
                            sx={{ 
                              borderBottom: "1px solid #ffffffff",
                              fontWeight: "bold",
                              fontSize: {
                                xs: "6px",
                                sm: "8px",
                                md: "9px",
                              },
                              maxWidth: "15px",
                              padding: 0,
                              textAlign: "center"
                            }} 
                          >
                            {p}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableHead>
                {returnData?.exclDiv?.length > 0 &&
                  returnData?.inclDiv?.length > 0 && (
                <TableBody>
                {[
                    { label: "Excl Div" , field: "exclDiv" },
                    { label: "Incl Div", field: "inclDiv" },       
                ].map((rowItem, rowIdx) => (
                    <TableRow
                    key={rowIdx}
                    sx={{
                        "& td": { 
                            fontSize: {
                              xs: "6px",
                              sm: "8px",
                              md: "9px",
                            }, 
                            borderBottom: "1px solid #ffffffff", 
                            maxWidth: "15px",
                            padding: 0,
                            },
                        whiteSpace: 'nowrap',
                    }}
                    >
                    {/* Render the row label as first cell */}
                    <TableCell sx={{ 
                        position: "sticky",
                        backgroundColor: "white",
                        left: 0, 
                        zIndex: 2,
                        height: 15,
                        textAlign : "left",
                        padding: 0
                        }}
                    >
                        {(rowItem.label)}
                    </TableCell>

                    {/* Render the financial data cells */}
                    {returnData[rowItem.field].map((value, colIdx) => (
                        <TableCell key={colIdx} sx={{ 
                        color: (() => {
                              if (isNegative(value)) {
                                return "red"; // red only when not greyed out
                              } else if (value === "52 Wk High" || value === "52 Wk Low") {
                                return "black";
                              }
                              return "green";
                            }),
                        textAlign: "center",
                        }}>
                          {value === "52 Wk High" || value === "52 Wk Low" ? value : formatCurrency(value)}
                        </TableCell>
                    ))}
                    </TableRow>
                ))}
                </TableBody>
                )}
            </Table>
        </TableContainer>
    )
});


export default ReturnsTable